// src/utils/exportCheckForBaseline.js
//
// "Promote this check into the committed baseline" — export path, not a write
// path. proxy-server/conformance/suiteStore.js deliberately rejects any write
// into a committed suite (see appendCheck()): the baseline
// (proxy-server/conformance/suites/adtech-baseline.checks.json) stays a
// git-reviewed artifact. A user who proves out a check in the dashboard has no
// route past hand-copying JSON. This module is that route's ONLY logic: given
// a Check object, produce (a) JSON formatted to match the baseline file's
// existing convention so a diff reads clean, (b) a ready-to-paste commit
// message, and (c) warnings when the check looks app-specific rather than
// spec-level. It never touches disk, never calls the network, and never
// writes anything — the caller copies the JSON out by hand and opens a PR.
// git review remains the actual gate.
//
// Pure + framework-free on purpose (no React import) so it is unit-testable
// without mounting anything, and reusable from both CheckSpecPanel.jsx and
// tests.
//
// Never throws: buildBaselineExport() always returns an object, even on
// garbage input.

/* ── light shape gate ─────────────────────────────────────────────────────
 * Deliberately NOT a port of proxy-server/conformance/compileCheck.js's
 * validateCheckShape() — that is server-side CommonJS (unreachable from a
 * CRA bundle without breaking the service boundary) and validates far more
 * than an export button needs to gate on: enum membership for every source/
 * entity/state, operator-specific expectation fields (equals -> value,
 * oneOf -> values, matches -> pattern, range -> range[2], ...), requestType
 * spelling against the live classifier's known-type list, etc. All of that
 * still runs for real the moment this JSON is pasted into the baseline and
 * exercised (or if someone routes it through suiteStore.saveUserSuite
 * first) — this gate only needs to catch "not a Check at all" garbage before
 * offering to export it, per the task's explicit scope: id present, target
 * present, assert present with a recognised mode/op.
 *
 * MODES/OPERATORS below mirror proxy-server/conformance/schemaContext.js
 * (the authoritative vocab) as of this writing. Keep loosely in sync, but a
 * drift here only ever WIDENS or NARROWS this shallow pre-check — it can't
 * let something unsound reach the real baseline, because the real baseline
 * is git-reviewed JSON a human reads before merging.
 */
const KNOWN_MODES = ['each', 'all', 'exists', 'absent', 'count', 'ratio', 'noDuplicates', 'minInterval', 'uniform', 'coherent'];

// Modes whose comparison lives in mode-specific fields rather than a top-level
// `assert.op`. Derived from the real baseline suite, not invented — see the
// note in validateCheckShapeLight(). `count` is deliberately NOT here: every
// count check in the baseline does carry a top-level op.
// `coherent` belongs here for the same reason: its comparison lives in
// `probes[]`, each entry carrying its own source/param, and the server-side
// validator REJECTS a top-level op on it outright.
const SESSION_LEVEL_MODES = ['noDuplicates', 'minInterval', 'uniform', 'ratio', 'coherent'];
const KNOWN_OPS = ['present', 'absent', 'equals', 'oneOf', 'matches', 'notMatches', 'gt', 'lt', 'range', 'inBucket', 'validValue'];

function isPlainObject(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/**
 * validateCheckShapeLight(check) -> { ok, issues: string[] }
 * The few structural essentials only — see the block comment above for what
 * this deliberately does NOT check.
 */
export function validateCheckShapeLight(check) {
  const issues = [];
  try {
    if (!isPlainObject(check)) {
      return { ok: false, issues: ['check is not an object'] };
    }
    if (typeof check.id !== 'string' || !check.id.trim()) {
      issues.push('missing id (must be a non-empty string)');
    }
    if (!isPlainObject(check.target)) {
      issues.push('missing target');
    }
    if (!isPlainObject(check.assert)) {
      issues.push('missing assert');
    } else {
      const { mode, op, allow } = check.assert;
      if (mode !== undefined && mode !== null && !KNOWN_MODES.includes(mode)) {
        issues.push(`assert.mode "${mode}" is not a recognised mode`);
      }
      const isAllowlistForm = mode === 'absent' && Array.isArray(allow);
      // Session-level modes carry their comparison in mode-specific fields, not
      // in a top-level `assert.op` — requiring one here wrongly rejected 8 real
      // checks in adtech-baseline.checks.json (baseline-cachebuster-unique,
      // -duplicate-gam-auction, -refresh-pacing, -slow-ad-requests,
      // -consent-uniform-tcf, -consent-uniform-usprivacy, -coppa-uniform,
      // -gdpr-uniform). Shapes:
      //   noDuplicates -> keyParams[]        minInterval -> ms, groupBy
      //   uniform      -> source, param      ratio       -> of{..op..}, cmp{..op..}
      // A false BLOCKING error is worse than a false warning: it stops a
      // legitimate promotion outright.
      const isSessionLevelMode = SESSION_LEVEL_MODES.includes(mode);
      if (!isAllowlistForm && !isSessionLevelMode) {
        if (op === undefined || op === null || op === '') {
          issues.push('assert.op is missing (or assert must be the mode:"absent" + allow[] allowlist form)');
        } else if (!KNOWN_OPS.includes(op)) {
          issues.push(`assert.op "${op}" is not a recognised operator`);
        }
      } else if (isSessionLevelMode && op !== undefined && op !== null && op !== '' && !KNOWN_OPS.includes(op)) {
        // A session-level mode MAY still carry an op; if it does, it must be real.
        issues.push(`assert.op "${op}" is not a recognised operator`);
      }
    }
  } catch (e) {
    return { ok: false, issues: [`shape check threw: ${e?.message || e}`] };
  }
  return { ok: issues.length === 0, issues };
}

/* ── baseline JSON formatting convention ─────────────────────────────────
 * Read straight off proxy-server/conformance/suites/adtech-baseline.checks.json
 * (138 checks, all sharing this exact shape): the file is a JSON array,
 * pretty-printed at 2-space indent, and EVERY check object has the same five
 * keys in the same order — id, description, target, assert, severity. The
 * "target" and "assert" VALUES are themselves compact (no added whitespace —
 * plain JSON.stringify(value), not JSON.stringify(value, null, 2)); only the
 * outer check object is indented. That two-tier style (pretty outer object,
 * compact nested target/assert) is what a prior commit meant by "restore
 * compact suite formatting" — this reproduces it exactly rather than emitting
 * JSON.stringify(check, null, 2), which would fully pretty-print target/assert
 * too and NOT match a single line of the real file.
 */
const BASELINE_KEY_ORDER = ['id', 'description', 'target', 'assert', 'severity'];

/**
 * formatCheckForBaseline(check) -> string
 * One check object, indented exactly as it appears as an ELEMENT of the
 * baseline array (2-space brace, 4-space keys) — so it can be pasted
 * straight in after an existing entry's `},` and read identically to its
 * neighbours in the diff, needing only a comma added by hand. Deliberately
 * NOT re-indented to column 0, since the target is always an insertion into
 * the existing array, never a standalone file.
 */
export function formatCheckForBaseline(check) {
  const seen = new Set();
  const orderedKeys = [];
  for (const k of BASELINE_KEY_ORDER) {
    if (Object.prototype.hasOwnProperty.call(check, k)) {
      orderedKeys.push(k);
      seen.add(k);
    }
  }
  // Preserve any extra/unknown fields rather than silently dropping them —
  // appended after the canonical five, in their original order.
  for (const k of Object.keys(check)) {
    if (!seen.has(k)) orderedKeys.push(k);
  }
  const lines = [];
  for (const k of orderedKeys) {
    const serialized = JSON.stringify(check[k]); // compact — matches target/assert style
    if (serialized === undefined) continue; // e.g. an explicit `undefined` value
    lines.push(`    ${JSON.stringify(k)}: ${serialized}`);
  }
  return `  {\n${lines.join(',\n')}\n  }`;
}

/* ── commit message ───────────────────────────────────────────────────────
 * Subject convention lifted from `git log --oneline`: "<type>(<scope>): <what,
 * lower-case, imperative>" (e.g. "feat(checks): vpos, player size, ..."). A
 * baseline promotion is always additive from this tool's point of view, so
 * the type is fixed at "feat" and the scope at "checks" (the baseline lives
 * under conformance/). The body is built ONLY from the check's own
 * description/target/assert — no invented claims about what it verified.
 */
function assertSubjectPlain(assert) {
  const source = typeof assert?.source === 'string' ? assert.source : '';
  const param = (assert?.param !== undefined && assert?.param !== null && assert.param !== '') ? String(assert.param) : '';
  if (source && param) return `${source}.${param}`;
  return source || param || 'the request';
}

function describeOpPlain(assert) {
  const op = typeof assert?.op === 'string' ? assert.op : '';
  switch (op) {
    case 'present': return 'is present';
    case 'absent': return (Array.isArray(assert.allow) && assert.allow.length)
      ? `has nothing outside the allowlist [${assert.allow.join(', ')}]`
      : 'is absent';
    case 'equals': return `equals ${JSON.stringify(assert.value)}`;
    case 'oneOf': return `is one of ${JSON.stringify(assert.values)}`;
    case 'matches': return `matches /${assert.pattern}/`;
    case 'notMatches': return `does NOT match /${assert.pattern}/`;
    case 'gt': return `is greater than ${assert.value}`;
    case 'lt': return `is less than ${assert.value}`;
    case 'range': return `is within ${Array.isArray(assert.range) ? assert.range.join('–') : String(assert.range ?? '')}`;
    case 'inBucket': return 'falls on a configured price bucket';
    case 'validValue': return "is a valid value for its vendor spec";
    default: return op ? `${op}s` : 'is checked';
  }
}

function describeTargetPlain(target) {
  if (!isPlainObject(target)) return 'the in-scope requests';
  if (target.entity) return `derived ${target.entity} entities`;
  const parts = [];
  const rt = Array.isArray(target.requestType) ? target.requestType : (target.requestType ? [target.requestType] : []);
  const ac = Array.isArray(target.adCategory) ? target.adCategory : (target.adCategory ? [target.adCategory] : []);
  if (rt.length) parts.push(`${rt.join('/')} requests`);
  if (ac.length) parts.push(`${ac.join('/')} traffic`);
  return parts.length ? parts.join(' + ') : 'the in-scope requests';
}

/**
 * buildCommitMessage(check) -> string
 * "<subject>\n\n<body>" — ready to pass straight to `git commit -m`.
 */
export function buildCommitMessage(check) {
  const id = (check && typeof check.id === 'string' && check.id.trim()) ? check.id.trim() : '(unnamed check)';
  const subject = `feat(checks): promote ${id} to the baseline suite`;

  const bodyLines = [];
  const desc = (check && typeof check.description === 'string' && check.description.trim())
    ? check.description.trim()
    : null;
  if (desc) bodyLines.push(desc);

  const assert = check && check.assert;
  if (isPlainObject(assert)) {
    const mode = typeof assert.mode === 'string' ? assert.mode : 'each';
    const subject2 = assertSubjectPlain(assert);
    const pred = describeOpPlain(assert);
    const scope = describeTargetPlain(check && check.target);
    bodyLines.push(`Asserts (mode: ${mode}) on ${scope}: ${subject2} ${pred}.`);
  } else {
    bodyLines.push('Asserts: (malformed — no assert block).');
  }

  const severity = (check && typeof check.severity === 'string' && check.severity) ? check.severity : 'unspecified';
  bodyLines.push(`Severity: ${severity}.`);

  return `${subject}\n\n${bodyLines.join('\n\n')}`;
}

/* ── app-specificity warnings ──────────────────────────────────────────────
 * The part that matters: the baseline is app-agnostic by design (this tool
 * was deliberately "de-Foxed" — see docs/checks-ai-suite-and-defox handoff).
 * A promotion path that lets publisher specifics leak into shared code would
 * undo that. Every detector below is a NARROW, high-precision structural
 * signal chosen so a legitimate spec-level check does not trip it — verified
 * against the real baseline (138 checks, zero literal IU paths, zero literal
 * bundle ids, zero literal cust_params value comparisons, and exactly one
 * literal host — a well-known Amazon ad-tech host — anywhere in the file).
 * Each warning names where the check belongs instead.
 */

// Ad-tech vendor/spec hosts already legitimately literal in shared code
// (baseline + mediationNetworks/appAdsCheck registries). NOT publisher
// content domains. A host outside this list reads as someone's own site.
const KNOWN_AD_HOSTS = [
  'doubleclick.net', 'googlesyndication.com', 'googletagservices.com', 'googleadservices.com', 'google.com',
  'gstatic.com', 'amazon-adsystem.com', 'amazon.com', 'springserve.com', 'adsafeprotected.com', 'moatads.com',
  'doubleverify.com', 'ias.net', 'iasds01.com', 'freewheel.tv', 'fwmrm.net', 'adnxs.com', 'criteo.com',
  'pubmatic.com', 'rubiconproject.com', 'openx.net', 'indexexchange.com', 'contextweb.com', 'casalemedia.com',
  'smartadserver.com', 'liftoff.io', 'applovin.com', 'unity3d.com', 'unityads.unity3d.com', 'mintegral.com',
  'pangleglobal.com', 'chartboost.com', 'vungle.com', 'ironsrc.com', 'media.net', 'sharethrough.com',
  'yahoo.com', 'verizonmedia.com', 'adition.com', 'improvedigital.com', 'triplelift.com', 'appnexus.com',
];

const IU_PATH_RE = /^\/?\d{4,}(\/[a-zA-Z0-9_.-]+){2,}\/?$/; // e.g. /21674100491/brand/platform/adunit
const NETWORK_PARAM_RE = /network/i;
const IU_PARAM_RE = /(^|[._])(iu|adunit|ad_unit|unitpath|slot)([._]|$)/i;
const BUNDLE_PARAM_RE = /(bundle|package|publisher.?name|app.?id|appname)/i;
const REVERSE_DOMAIN_RE = /^[a-z0-9]+(\.[a-z0-9_-]+){2,}$/i; // e.g. com.foxnews.mobile
const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;
const BARE_ID_RE = /^\d{4,}$/;

function isKnownAdHost(host) {
  const h = String(host).toLowerCase();
  return KNOWN_AD_HOSTS.some((k) => h === k || h.endsWith(`.${k}`));
}

/** Every string literal a target.where / assert / assert.then expression could carry. */
function literalStrings(expr) {
  const out = [];
  const push = (v) => { if (v !== undefined && v !== null && v !== '') out.push(String(v)); };
  if (expr.value !== undefined) push(expr.value);
  if (Array.isArray(expr.values)) expr.values.forEach(push);
  if (typeof expr.pattern === 'string') push(expr.pattern);
  if (Array.isArray(expr.allow)) expr.allow.forEach(push);
  return out;
}

/** Collect every {source,param,op,value,values,pattern,allow} expression on a check. */
function collectExprs(check) {
  const exprs = [];
  const t = check && check.target;
  if (isPlainObject(t)) (Array.isArray(t.where) ? t.where : [t.where]).forEach((p) => { if (isPlainObject(p)) exprs.push(p); });
  const a = check && check.assert;
  if (isPlainObject(a)) {
    exprs.push(a);
    if (isPlainObject(a.then)) exprs.push(a.then);
  }
  return exprs;
}

function signalHardcodedIuOrNetwork(expr) {
  const candidates = literalStrings(expr);
  const iuLike = candidates.find((v) => IU_PATH_RE.test(v));
  if (iuLike) {
    return `Literal value "${iuLike}" (param "${expr.param || ''}") looks like a full GAM IU path (network id + brand/platform segments) — that is one publisher's ad-unit tree. Belongs in a user suite (data/check-suites/), or — if it's genuinely a structural rule about IU shape — parameterise it via the App Pack's identity.networkId / iuGrammar instead of a literal in the check.`;
  }
  if (NETWORK_PARAM_RE.test(String(expr.param || '')) || IU_PARAM_RE.test(String(expr.param || ''))) {
    const idLike = candidates.find((v) => BARE_ID_RE.test(v));
    if (idLike && ['equals', 'oneOf'].includes(expr.op)) {
      return `Literal network/ad-unit id "${idLike}" (param "${expr.param || ''}") identifies one publisher's account. Belongs in a user suite, or reference it via the App Pack's identity.networkId instead of hardcoding it.`;
    }
  }
  return null;
}

function signalBundleOrPackage(expr) {
  if (!BUNDLE_PARAM_RE.test(String(expr.param || ''))) return null;
  if (!['equals', 'oneOf', 'matches', 'notMatches'].includes(expr.op)) return null;
  const candidates = literalStrings(expr);
  const hit = candidates.find((v) => REVERSE_DOMAIN_RE.test(v));
  if (!hit) return null;
  return `Literal bundle/package id "${hit}" (param "${expr.param || ''}") identifies one publisher's app build. Belongs in a user suite, or the App Pack's stack.profiles[...].bundleId instead of hardcoding it in the shared baseline.`;
}

function signalCustParamBusinessRule(expr) {
  const source = expr.source;
  const param = String(expr.param || '');
  const isCustParam = source === 'custParams' || /^cust_?params\./i.test(param);
  if (!isCustParam) return null;
  if (!expr.op || ['present', 'absent'].includes(expr.op)) return null; // presence-only is structural, not a value rule
  const candidates = literalStrings(expr);
  if (!candidates.length) return null;
  return `Asserts a specific cust_params value ("${expr.param || ''}" ${expr.op} ${JSON.stringify(candidates[0])}) — cust_params are publisher-defined ad-ops targeting keys with no spec behind them, so a literal expected value here reads as a publisher's business rule, not a conformance requirement. Belongs in a user suite, or the App Pack's custParamRules.`;
}

function collectHostCandidates(check, exprs) {
  const hosts = [];
  const t = check && check.target;
  if (isPlainObject(t)) {
    if (typeof t.host === 'string') hosts.push(t.host);
    if (Array.isArray(t.host)) t.host.forEach((h) => { if (typeof h === 'string') hosts.push(h); });
  }
  for (const expr of exprs) {
    if (expr.source === 'host' && ['equals', 'oneOf'].includes(expr.op)) {
      literalStrings(expr).forEach((v) => hosts.push(v));
    }
  }
  return hosts;
}

function signalPublisherHost(hostCandidates) {
  for (const h of hostCandidates) {
    if (DOMAIN_RE.test(h) && !isKnownAdHost(h)) {
      return `Literal host "${h}" isn't a recognised ad-tech vendor host — it reads like a publisher's own content/page domain rather than a spec-level vendor. Belongs in a user suite, or the App Pack's contentApis.contentHosts/videoHosts instead of a shared check.`;
    }
  }
  return null;
}

/**
 * detectAppSpecificWarnings(check) -> string[]
 * Deliberately conservative — see the block comment above. Never throws.
 */
export function detectAppSpecificWarnings(check) {
  const warnings = [];
  try {
    if (!isPlainObject(check)) return warnings;
    const exprs = collectExprs(check);
    for (const expr of exprs) {
      if (!isPlainObject(expr)) continue;
      const iu = signalHardcodedIuOrNetwork(expr);
      if (iu) warnings.push(iu);
      const bundle = signalBundleOrPackage(expr);
      if (bundle) warnings.push(bundle);
      const cust = signalCustParamBusinessRule(expr);
      if (cust) warnings.push(cust);
    }
    const hostWarning = signalPublisherHost(collectHostCandidates(check, exprs));
    if (hostWarning) warnings.push(hostWarning);
  } catch {
    // A warning detector must never block export on its own bug — fail quiet.
  }
  return Array.from(new Set(warnings));
}

/* ── entry point ───────────────────────────────────────────────────────── */

/**
 * buildBaselineExport(check) ->
 *   { ok, error, json, commitMessage, warnings }
 * ok:false means a BLOCKING shape problem (see validateCheckShapeLight) —
 * json/commitMessage are null and `error` is a human-readable reason. ok:true
 * always includes json + commitMessage; `warnings` is an app-specificity
 * advisory list that may be non-empty even when ok:true (a promotable-shape
 * check can still be the wrong content for a shared baseline).
 * Never throws.
 */
export function buildBaselineExport(check) {
  try {
    const shape = validateCheckShapeLight(check);
    if (!shape.ok) {
      return { ok: false, error: shape.issues.join('; '), json: null, commitMessage: null, warnings: [] };
    }
    const json = formatCheckForBaseline(check);
    const commitMessage = buildCommitMessage(check);
    const warnings = detectAppSpecificWarnings(check);
    return { ok: true, error: null, json, commitMessage, warnings };
  } catch (e) {
    return { ok: false, error: `export failed: ${e?.message || e}`, json: null, commitMessage: null, warnings: [] };
  }
}
